import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { DEFAULT_CATEGORIES, type TodoColorKey } from '@/lib/todoColors';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface TodoTask {
  id: string;
  category_id: string;
  content: string;
  done: boolean;
  position: number;
  done_at: string | null;
}

export interface TodoCategory {
  id: string;
  name: string;
  color: TodoColorKey;
  position: number;
  tasks: TodoTask[];
}

// ─── Tradutor ─────────────────────────────────────────────────────────────────

function translateTodoError(msg: string): string {
  if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('new row violates'))
    return 'Permissão insuficiente. Verifique se você está autenticado.';
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError'))
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  return 'Ocorreu um erro inesperado. Tente novamente.';
}

// ─── Hook principal ───────────────────────────────────────────────────────────

export function useTodos() {
  const { tenantId }  = useTenant();
  const { user }      = useAuth();
  const [categories, setCategories] = useState<TodoCategory[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId || !user) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    const { data: cats, error: catsErr } = await supabase
      .from('todo_categories')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('user_id', user.id)
      .order('position');

    if (catsErr) { setError('Erro ao carregar categorias.'); setLoading(false); return; }

    // Seed das categorias padrão na primeira vez
    if ((cats ?? []).length === 0) {
      const seeds = DEFAULT_CATEGORIES.map((c, i) => ({
        tenant_id: tenantId,
        user_id:   user.id,
        name:      c.name,
        color:     c.color,
        position:  i,
      }));
      await supabase.from('todo_categories').insert(seeds as never);
      const { data: seeded } = await supabase
        .from('todo_categories')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('user_id', user.id)
        .order('position');
      setCategories(((seeded ?? []) as any[]).map((c) => ({ ...c, color: c.color as TodoColorKey, tasks: [] })));
      setLoading(false);
      return;
    }

    const catIds = (cats as any[]).map((c) => c.id as string);

    const { data: tasks, error: tasksErr } = await supabase
      .from('todo_tasks')
      .select('*')
      .in('category_id', catIds)
      .order('position');

    if (tasksErr) { setError('Erro ao carregar tarefas.'); setLoading(false); return; }

    const result: TodoCategory[] = (cats as any[]).map((c) => ({
      id:       c.id as string,
      name:     c.name as string,
      color:    c.color as TodoColorKey,
      position: c.position as number,
      tasks:    ((tasks ?? []) as any[])
        .filter((t) => t.category_id === c.id)
        .map((t) => ({
          id:          t.id as string,
          category_id: t.category_id as string,
          content:     t.content as string,
          done:        t.done as boolean,
          position:    t.position as number,
          done_at:     t.done_at as string | null,
        })),
    }));

    setCategories(result);
    setLoading(false);
  }, [tenantId, user]);

  useEffect(() => { void load(); }, [load]);

  const pendingCount = categories.flatMap((c) => c.tasks).filter((t) => !t.done).length;

  // ─── Categorias ───────────────────────────────────────────────────────────

  async function addCategory(name: string, color: TodoColorKey): Promise<void> {
    if (!tenantId || !user) throw new Error('Sem tenant.');
    const position = categories.length > 0
      ? Math.max(...categories.map((c) => c.position)) + 1
      : 0;
    // Atualização otimista (sem load() completo — evita o loading global
    // que desmontava o widget flutuante e causava o "piscar" na tela)
    const { data, error: err } = await supabase
      .from('todo_categories')
      .insert({ tenant_id: tenantId, user_id: user.id, name, color, position } as never)
      .select('id')
      .single();
    if (err) throw new Error(translateTodoError(err.message));
    const newCat: TodoCategory = { id: (data as any).id, name, color, position, tasks: [] };
    setCategories((prev) => [...prev, newCat]);
  }

  async function renameCategory(id: string, name: string): Promise<void> {
    const { error: err } = await supabase
      .from('todo_categories').update({ name } as never).eq('id', id);
    if (err) throw new Error(translateTodoError(err.message));
    setCategories((prev) => prev.map((c) => c.id === id ? { ...c, name } : c));
  }

  async function setCategoryColor(id: string, color: TodoColorKey): Promise<void> {
    const { error: err } = await supabase
      .from('todo_categories').update({ color } as never).eq('id', id);
    if (err) throw new Error(translateTodoError(err.message));
    setCategories((prev) => prev.map((c) => c.id === id ? { ...c, color } : c));
  }

  async function deleteCategory(id: string): Promise<void> {
    const { error: err } = await supabase.from('todo_categories').delete().eq('id', id);
    if (err) throw new Error(translateTodoError(err.message));
    setCategories((prev) => prev.filter((c) => c.id !== id));
  }

  // ─── Tarefas ──────────────────────────────────────────────────────────────

  async function addTask(categoryId: string, content: string): Promise<void> {
    if (!tenantId || !user) throw new Error('Sem tenant.');
    const cat = categories.find((c) => c.id === categoryId);
    const position = cat ? cat.tasks.length : 0;
    const { data, error: err } = await supabase
      .from('todo_tasks')
      .insert({ tenant_id: tenantId, user_id: user.id, category_id: categoryId, content, position } as never)
      .select('id')
      .single();
    if (err) throw new Error(translateTodoError(err.message));
    const newTask: TodoTask = {
      id: (data as any).id, category_id: categoryId, content, done: false, position, done_at: null,
    };
    setCategories((prev) =>
      prev.map((c) => c.id === categoryId ? { ...c, tasks: [...c.tasks, newTask] } : c)
    );
  }

  async function toggleTask(id: string): Promise<void> {
    const task = categories.flatMap((c) => c.tasks).find((t) => t.id === id);
    if (!task) return;
    const done    = !task.done;
    const done_at = done ? new Date().toISOString() : null;
    const { error: err } = await supabase
      .from('todo_tasks').update({ done, done_at } as never).eq('id', id);
    if (err) throw new Error(translateTodoError(err.message));
    setCategories((prev) =>
      prev.map((c) => ({
        ...c,
        tasks: c.tasks.map((t) => t.id === id ? { ...t, done, done_at } : t),
      }))
    );
  }

  async function editTask(id: string, content: string): Promise<void> {
    const { error: err } = await supabase
      .from('todo_tasks').update({ content } as never).eq('id', id);
    if (err) throw new Error(translateTodoError(err.message));
    setCategories((prev) =>
      prev.map((c) => ({
        ...c,
        tasks: c.tasks.map((t) => t.id === id ? { ...t, content } : t),
      }))
    );
  }

  async function deleteTask(id: string): Promise<void> {
    const { error: err } = await supabase.from('todo_tasks').delete().eq('id', id);
    if (err) throw new Error(translateTodoError(err.message));
    setCategories((prev) =>
      prev.map((c) => ({ ...c, tasks: c.tasks.filter((t) => t.id !== id) }))
    );
  }

  return {
    categories, loading, error, reload: load, pendingCount,
    addCategory, renameCategory, setCategoryColor, deleteCategory,
    addTask, toggleTask, editTask, deleteTask,
  };
}
